import { KinematicBodyFacet } from 'engine/facets/KinematicBodyFacet';
import { addV2, getDistance, newV2, scaleV2 } from 'engine/lib/V2';
import { Laserable } from 'game/behaviors/Laserable';
import { Waterable } from 'game/behaviors/Waterable';
import { MAX_LASER_RANGE } from 'game/config';
import { BotToolChoice, InventoryItem, InventorySelection, ProcessError, ProcessPayload, ProcessStatus, UseModule, WATERING_CAN_MAX, determineTool } from 'game/state/BotState';
import clamp from 'lodash/clamp';
import round from 'lodash/round';
import { Processor, ProcessorContext } from '../Processor';

const err = (error: ProcessError): ProcessStatus => ({
  status: 'error',
  error,
});

type UseProcessPayload = Extract<ProcessPayload, { action: 'use' }>;
type ToolProcessor = (
  tool: InventoryItem,
  payload: UseProcessPayload,
  module: UseModule,
  ctx: ProcessorContext,
) => ProcessStatus | void;

const toolProcessors: Record<BotToolChoice, ToolProcessor> = {

  [BotToolChoice.Axe]: () => err({ type: 'unclear' }),

  [BotToolChoice.Laser]: (inventoryTool, payload, module, ctx) => {
    const laserable = payload.target.getFacet(Laserable);
    if (!laserable) return err({ type: 'unclear' });
    if (laserable.props.didSucceed()) {
      return { status: 'success', value: payload, moduleId: module.id };
    }
    const { position } = ctx.bot.entity.transform;
    const d = getDistance(position, payload.target.transform.position);
    if (d <= MAX_LASER_RANGE) {
      payload.enRoute = false;
      return { status: 'running', value: payload };
    }
  },

  [BotToolChoice.WateringCan]: (inventoryTool, payload, module, ctx) => {
    const waterable = payload.target.getFacet(Waterable);
    if (!waterable) return err({ type: 'unclear' });
    const amountNeeded = waterable.props.amountNeeded();
    if (inventoryTool.count < amountNeeded) {
      return err({ type: 'missing', item: InventorySelection.Water });
    }
    const { position } = ctx.bot.entity.transform;
    payload.dest = addV2(payload.dest, waterable.props.offset(ctx.bot.entity));
    const d = getDistance(position, payload.dest);
    if (d <= waterable.props.maxDistance) {
      payload.enRoute = false;
      if (waterable.props.isWaterSource) {
        const amount = (ctx.delta / 1000) * 7;
        inventoryTool.count = clamp(inventoryTool.count + amount, WATERING_CAN_MAX);
        if (inventoryTool.count === WATERING_CAN_MAX) {
          return { status: 'success', moduleId: module.id, value: payload };
        }
        return { status: 'running', value: payload };
      }
      const amountToGive = round(clamp(ctx.delta / 1100, amountNeeded), 2);
      inventoryTool.count = round(inventoryTool.count - amountToGive, 2);
      const complete = waterable.props.water(amountToGive, ctx.engine);
      if (complete) {
        return { status: 'success', value: payload, moduleId: module.id };
      }
      return { status: 'running', value: payload };
    }
  },
};

export const useProcessor: Processor<UseModule> = (module, ctx) => {
  const { target } = module.inputs;
  const { modules, inventory, botSpeed } = ctx.bot.props.state;
  const targetModule = target && modules.find(m => m.id === target.moduleId);
  if (!targetModule) return { status: 'null' };
  const targetResult = ctx.processOther(targetModule, ctx);
  if (targetResult.status !== 'success' || targetResult.value.type !== 'entity') {
    return { status: 'null' };
  }
  const tool = module.config.tool || determineTool(module, ctx.bot.props.state);
  if (!tool) return { status: 'null' };
  const body = ctx.bot.entity.getFacet(KinematicBodyFacet);
  if (!body) return { status: 'null' };

  const inventoryTool = inventory.find(item => item.type === tool);
  if (!inventoryTool) {
    return err({ type: 'missing', item: tool });
  }

  const payload: ProcessPayload = {
    type: 'command',
    action: 'use',
    target: targetResult.value.entity,
    dest: [...targetResult.value.entity.transform.position],
    tool,
    enRoute: true,
  };

  const res = toolProcessors[inventoryTool.type](inventoryTool, payload, module, ctx);
  if (res) return res;

  const [x1, y1] = ctx.bot.entity.transform.position;
  const [x2, y2] = payload.dest;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const v = newV2(Math.cos(angle), Math.sin(angle));
  body.move(scaleV2(v, botSpeed * ctx.delta), ctx.engine);
  return { status: 'running', value: payload };
};
